import React from 'react';
import {
  INTERVAL_1_MIN,
  SL_ORDER_PLACEMENT_ADVANCE,
  SL_ORDER_PLACEMENT_LATER,
  ORDER_TYPE_M,
} from '@quantiply/common/constants';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAlgoEditConfigConfigurations,
  getAlgoEditConfigLegs,
} from '../../../stores/algo-edit-config/getters';
import useAlgoRunning from '../../../hooks/useAlgoRunning';
import useAlgoValidationError from '../../../hooks/useAlgoValidationError';
import ElevatedSelectControlled from '../../../components/Basic/ElevatedSelectControlled';
import { configurationDataUpdate } from '../../../stores/algo-edit-config/actions';
import { useMemo } from 'react';
import useAlgoLoading from '../../../hooks/useAlgoLoading';
import BubbleGridRow from '../../../components/BubbleGridRow';
import useModifyWhileLive from '../../../hooks/useModifyWhileLive';
import TimeSelectorGeneral from '../../../components/TimeSelectorGeneral';
import GridGap from '../../../components/GridGap';

export const jIntervalTypeCandleClose = INTERVAL_1_MIN;

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up('lg')]: {
      marginTop: 8,
    },
  },
  space: {
    marginBottom: 30,
    [theme.breakpoints.up('lg')]: {
      marginBottom: 0,
    },
  },
  JourneyInterval: {
    marginBottom: 16,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 220,
      marginRight: 40,
      marginBottom: 0,
    },
  },
  JOrderPlacement: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: 319,
    },
  },
  JEntryAfter: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: 294,
    },
  },
  JEntryBefore: {
    marginBottom: 16,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 294,
      marginRight: 30,
      marginBottom: 0,
    },
  },
}));

function useIsAnyJourney() {
  const isAnyJourney = useSelector((state) => {
    const legs = getAlgoEditConfigLegs(state);
    const isAnyJourney = legs.some((leg) => leg.isJourney);

    return isAnyJourney;
  });

  return isAnyJourney;
}

const JEntryAfter = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const algoRunning = useAlgoRunning();
  const modifyWhileLive = useModifyWhileLive();
  const loading = useAlgoLoading();

  const jAfter = useSelector((state) => {
    const { jAfter = '' } = getAlgoEditConfigConfigurations(state);

    return jAfter;
  });

  const onChange = (val) => {
    dispatch(configurationDataUpdate('jAfter', val));
  };

  const onClear = () => {
    dispatch(configurationDataUpdate('jAfter', ''));
  };

  const [showError, helperText] = useAlgoValidationError(
    'configurations.jAfter'
  );

  return (
    <Grid item xs={12} lg={4} className={classes.JEntryAfter}>
      <TimeSelectorGeneral
        label="Journey entry only after (time):"
        name="jAfter"
        value={jAfter}
        error={showError}
        helperText={helperText}
        disabled={(algoRunning && !modifyWhileLive) || loading}
        onChange={onChange}
        onClear={onClear}
        showClearButton
        blankValue
      />
    </Grid>
  );
};

const JEntryBefore = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const algoRunning = useAlgoRunning();
  const modifyWhileLive = useModifyWhileLive();
  const loading = useAlgoLoading();

  const jBefore = useSelector((state) => {
    const { jBefore = '' } = getAlgoEditConfigConfigurations(state);

    return jBefore;
  });

  const onChange = (val) => {
    dispatch(configurationDataUpdate('jBefore', val));
  };

  const onClear = () => {
    dispatch(configurationDataUpdate('jBefore', ''));
  };

  const [showError, helperText] = useAlgoValidationError(
    'configurations.jBefore'
  );

  return (
    <Grid item xs={12} lg={4} className={classes.JEntryBefore}>
      <TimeSelectorGeneral
        label="Journey entry only before (time):"
        name="jBefore"
        value={jBefore}
        error={showError}
        helperText={helperText}
        disabled={(algoRunning && !modifyWhileLive) || loading}
        onChange={onChange}
        onClear={onClear}
        showClearButton
        blankValue
      />
    </Grid>
  );
};

const JOrderPlacement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const algoRunning = useAlgoRunning();
  const loading = useAlgoLoading();
  const jOrderPlacement = useSelector((state) => {
    const { jOrderPlacement = SL_ORDER_PLACEMENT_LATER } =
      getAlgoEditConfigConfigurations(state);

    return jOrderPlacement;
  });
  const jInterval = useSelector((state) => {
    const { jInterval = '' } = getAlgoEditConfigConfigurations(state);

    return jInterval;
  });

  const entryOrderType = useSelector((state) => {
    const { entryOrderType = ORDER_TYPE_M } =
      getAlgoEditConfigConfigurations(state);

    return entryOrderType;
  });

  const [showError, helperText] = useAlgoValidationError(
    'configurations.jOrderPlacement'
  );

  const onChange = (val) => {
    dispatch(configurationDataUpdate('jOrderPlacement', val));
  };

  const options = useMemo(
    () => [
      {
        value: SL_ORDER_PLACEMENT_ADVANCE,
        label: 'Advance',
      },
      {
        value: SL_ORDER_PLACEMENT_LATER,
        label: 'Later',
      },
    ],
    []
  );

  return (
    <Grid item xs={12} lg={4} className={classes.JOrderPlacement}>
      <ElevatedSelectControlled
        label="Journey Order Placement:"
        disableClearable
        name="jOrderPlacement"
        error={showError}
        helperText={helperText}
        disabled={
          algoRunning ||
          loading ||
          jInterval === jIntervalTypeCandleClose ||
          entryOrderType === ORDER_TYPE_M
        }
        value={jOrderPlacement}
        options={options}
        onChange={onChange}
      />
    </Grid>
  );
};

const JourneyInterval = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const algoRunning = useAlgoRunning();
  const loading = useAlgoLoading();
  const jInterval = useSelector((state) => {
    const { jInterval = '' } = getAlgoEditConfigConfigurations(state);

    return jInterval;
  });

  const [showError, helperText] = useAlgoValidationError(
    'configurations.jInterval'
  );

  const onChange = (val) => {
    dispatch(configurationDataUpdate('jInterval', val));
  };

  const options = useMemo(
    () => [
      {
        value: '',
        label: 'LTP',
      },
      {
        value: jIntervalTypeCandleClose,
        label: 'Candle close',
      },
    ],
    []
  );

  return (
    <Grid item xs={12} lg={4} className={classes.JourneyInterval}>
      <ElevatedSelectControlled
        label="Journey Method:"
        disableClearable
        name="jInterval"
        error={showError}
        helperText={helperText}
        disabled={algoRunning || loading}
        value={jInterval}
        options={options}
        onChange={onChange}
      />
    </Grid>
  );
};

const JourneyAdvancedSettings = () => {
  const classes = useStyles();
  const isAnyJourney = useIsAnyJourney();

  if (!isAnyJourney) {
    return <></>;
  }

  return (
    <>
      <Grid item xs={12} className={classes.root}>
        <BubbleGridRow>
          <JourneyInterval />
          <JEntryAfter />
          <JEntryBefore />
          <Grid item xs={12} className={classes.space} />
          <JOrderPlacement />
        </BubbleGridRow>
      </Grid>
      <GridGap />
    </>
  );
};

export default JourneyAdvancedSettings;
